import { inject, Injectable } from '@angular/core';
import { Label } from '@softapps/handel/esl/core';
import { ApiDataAccess } from '@softline/core';
import { lastValueFrom } from 'rxjs';
import {
  SOFTLINE_API_ESL_STORAGE_ASSOCIATE,
  SOFTLINE_API_ESL_STORAGE_CHANGE_LOCATION,
  SOFTLINE_API_ESL_STORAGE_CHECK_LOCATION,
  SOFTLINE_API_ESL_STORAGE_CONFIG,
  SOFTLINE_API_ESL_STORAGE_DISSOCIATE,
  SOFTLINE_API_ESL_STORAGE_LOCATION_SCAN,
  SOFTLINE_API_ESL_STORAGE_LOCATIONS,
  SOFTLINE_API_ESL_STORAGE_PREVIEW,
} from '../esl-link-storage.api';
import { CheckStorageLocation } from '../types/check-storage-location';
import { ProductStorageLocation } from '../types/product-storage-location';
import { StorageAssociate } from '../types/storage-associate';
import { StorageLocationScanResult } from '../types/storage-location-scan-result';

@Injectable()
export class EslLinkStorageRepository {
  readonly dataAccess = inject(ApiDataAccess);

  async getEslConfig(eslCode: string): Promise<Label> {
    return await this.dataAccess.get({
      path: SOFTLINE_API_ESL_STORAGE_CONFIG,
      pathParams: { eslCode },
    });
  }

  async checkItemStorageLocation(
    eslCode: string,
    body: ProductStorageLocation
  ): Promise<CheckStorageLocation> {
    return await this.dataAccess.create(
      {
        path: SOFTLINE_API_ESL_STORAGE_CHECK_LOCATION,
        pathParams: { eslCode },
      },
      body
    );
  }

  async changeItemStorageLocation(body: ProductStorageLocation): Promise<any> {
    return await this.dataAccess.create(
      {
        path: SOFTLINE_API_ESL_STORAGE_CHANGE_LOCATION,
      },
      body
    );
  }

  async storageAssociate(body: StorageAssociate): Promise<any> {
    return await this.dataAccess.create(
      {
        path: SOFTLINE_API_ESL_STORAGE_ASSOCIATE,
      },
      body
    );
  }

  async eslPreview(body: StorageAssociate): Promise<string> {
    const result = await lastValueFrom(
      this.dataAccess.download({ path: SOFTLINE_API_ESL_STORAGE_PREVIEW }, body)
    );
    if (result && result.type === 'response' && result.response) {
      let text: string;
      if (result.response instanceof Blob) {
        text = await result.response.text();
      } else if (result.response instanceof ArrayBuffer) {
        text = new TextDecoder().decode(result.response);
      } else {
        throw new Error('Unsupported response type');
      }
      const json = JSON.parse(text);
      const base64 = json.data;
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const imageBlob = new Blob([byteArray], { type: 'image/png' });
      return URL.createObjectURL(imageBlob);
    }
    return '';
  }

  async dissociate(eslCode: string): Promise<any> {
    return await this.dataAccess.create(
      {
        path: SOFTLINE_API_ESL_STORAGE_DISSOCIATE,
        pathParams: { eslCode },
      },
      {}
    );
  }

  async scanStorageLocation(
    storageLocationCode: string
  ): Promise<StorageLocationScanResult> {
    return await this.dataAccess.get({
      path: SOFTLINE_API_ESL_STORAGE_LOCATION_SCAN,
      pathParams: { storageLocationCode },
    });
  }

  async getAllStorageLocations(): Promise<any> {
    return await this.dataAccess.create(
      {
        path: SOFTLINE_API_ESL_STORAGE_LOCATIONS,
      },
      {
        body: { filter: '', multiValued: false, maxAbfrageResults: 1000, parameters: {} },
      }
    );
  }
}
